export const extractApplicantValidationMessages = (validationMessages: any): string[] => {
  const messages: string[] = [];
  if (!validationMessages) return messages;

  const pushMessage = (msg?: string, isDeleted?: boolean) => {
    if (!msg || msg.trim() === "" || isDeleted) return;
    messages.push(msg.trim());
  };

  if (Array.isArray(validationMessages)) {
    validationMessages.forEach((entry: any) => {
      const target = entry?.applicant ?? entry;
      pushMessage(target?.message, target?.isDeleted);
    });
    return messages;
  }

  if (typeof validationMessages === "object") {
    Object.values(validationMessages).forEach((arr: any) => {
      if (Array.isArray(arr)) {
        arr.forEach((entry: any) => {
          const target = entry?.applicant ?? entry;
          pushMessage(target?.message, target?.isDeleted);
        });
      }
    });
  }
  return messages;
};

type ProfileSection = {
  CaptionName?: string;
  DisplayName?: string;
  SectionKey?: string;
  state?: string | number;
  hasDocuments?: boolean;
};

type ProfileSectionIdentifier = {
  sectionKey: string;
  captionNames: readonly string[];
};

const SEND_MESSAGE_PROFILE_SECTIONS = {
  id: {
    sectionKey: "id",
    captionNames: ["US Driver's License"],
  },
  employmentIncome: {
    sectionKey: "employmentIncome",
    captionNames: ["Paystub"],
  },
  bankStatement: {
    sectionKey: "bankStatement",
    captionNames: ["Bank Statements"],
  },
} as const;

const LEGACY_PROFILE_SECTION_INDEXES: Record<string, number> = {
  employmentincome: 0,
  bankstatement: 1,
  id: 3,
};

const normalizeProfileSectionName = (value?: string) =>
  value?.trim().toLowerCase() ?? "";

const normalizeProfileSectionKey = (value?: string) =>
  value?.trim().toLowerCase() ?? "";

const VISIBLE_PROFILE_SECTION_STATES = new Set([1, 2]);

const hasProfileSectionIdentity = (section?: ProfileSection) => Boolean(
  normalizeProfileSectionKey(section?.SectionKey) ||
  normalizeProfileSectionName(section?.CaptionName) ||
  normalizeProfileSectionName(section?.DisplayName)
);

const isLegacyStatusOnlyProfileResponse = (sections?: ProfileSection[]) => {
  if (!sections?.length) return false;
  return sections.every((section) => !hasProfileSectionIdentity(section));
};

const findProfileSectionForMessages = (
  sections: ProfileSection[] | undefined,
  identifier: ProfileSectionIdentifier
) => {
  const sectionKey = normalizeProfileSectionKey(identifier.sectionKey);
  const captionNames = identifier.captionNames.map(normalizeProfileSectionName);
  const identifiedSection = sections?.find((item) => {
    const itemSectionKey = normalizeProfileSectionKey(item?.SectionKey);
    const captionName = normalizeProfileSectionName(item?.CaptionName);
    const displayName = normalizeProfileSectionName(item?.DisplayName);
    return itemSectionKey === sectionKey || captionNames.includes(captionName) || captionNames.includes(displayName);
  });

  if (identifiedSection || !isLegacyStatusOnlyProfileResponse(sections)) return identifiedSection;
  return sections?.[LEGACY_PROFILE_SECTION_INDEXES[sectionKey]];
};

const isProfileSectionEnabledForMessages = (
  sections: ProfileSection[] | undefined,
  identifier: ProfileSectionIdentifier,
  requiresDocuments = true
) => {
  const identifiedSection = findProfileSectionForMessages(sections, identifier);

  const normalizedState = Number(identifiedSection?.state);
  return (
    VISIBLE_PROFILE_SECTION_STATES.has(normalizedState) &&
    (!requiresDocuments || identifiedSection?.hasDocuments !== false)
  );
};

export const MESSAGE_PREAMBLE =
  'Thank you for submitting your proof documents. We found one or more issues with the documents you provided. Please return to Docuverus and provide the missing and/or correct documents noted below. Please note that your application cannot be processed until the missing or correct documents are provided.';

type PrefillSection = {
  label: string;
  messages: string[];
};

type ApplicantValidationResponses = {
  idValidation?: any;
  employerValidation?: any;
  bankValidation?: any;
  argylePayrollValidation?: any;
  argyleBankValidation?: any;
  plaidBankValidation?: any;
  plaidPayrollValidation?: any;
};

type ProfileControlledValidationSection = {
  profileSection: ProfileSectionIdentifier;
  label: string;
  requiresDocuments: boolean;
  omitWhenEmpty?: boolean;
  getMessages: (responses: ApplicantValidationResponses) => string[];
};

const PROFILE_CONTROLLED_VALIDATION_SECTIONS: ProfileControlledValidationSection[] = [
  {
    profileSection: SEND_MESSAGE_PROFILE_SECTIONS.id,
    label: "ID",
    requiresDocuments: true,
    getMessages: (responses: ApplicantValidationResponses) =>
      extractApplicantValidationMessages(responses.idValidation?.validation_message),
  },
  {
    profileSection: SEND_MESSAGE_PROFILE_SECTIONS.employmentIncome,
    label: "Employment Income",
    requiresDocuments: true,
    getMessages: (responses: ApplicantValidationResponses) =>
      extractApplicantValidationMessages(responses.employerValidation?.validation_messages),
  },
  {
    profileSection: SEND_MESSAGE_PROFILE_SECTIONS.employmentIncome,
    label: "Employment Income",
    requiresDocuments: false,
    omitWhenEmpty: true,
    getMessages: (responses: ApplicantValidationResponses) =>
      extractApplicantValidationMessages(responses.argylePayrollValidation?.validation_messages),
  },
  {
    profileSection: SEND_MESSAGE_PROFILE_SECTIONS.bankStatement,
    label: "Bank Statement",
    requiresDocuments: true,
    getMessages: (responses: ApplicantValidationResponses) =>
      extractApplicantValidationMessages(responses.bankValidation?.validation_messages),
  },
];

const PROFILE_INDEPENDENT_VALIDATION_SECTIONS = [
  {
    label: "Bank Linking",
    getMessages: (responses: ApplicantValidationResponses) =>
      extractApplicantValidationMessages(responses.argyleBankValidation?.validation_messages),
  },
  {
    label: "Plaid Bank Income",
    getMessages: (responses: ApplicantValidationResponses) =>
      extractApplicantValidationMessages(responses.plaidBankValidation?.validation_messages),
  },
  {
    label: "Plaid Payroll Income",
    getMessages: (responses: ApplicantValidationResponses) =>
      extractApplicantValidationMessages(responses.plaidPayrollValidation?.validation_messages),
  },
] as const;

export const collectApplicantValidationSections = ({
  idValidation,
  employerValidation,
  bankValidation,
  argylePayrollValidation,
  argyleBankValidation,
  plaidBankValidation,
  plaidPayrollValidation,
}: ApplicantValidationResponses, profileSections?: ProfileSection[]): PrefillSection[] => {
  const validationResponses = {
    idValidation,
    employerValidation,
    bankValidation,
    argylePayrollValidation,
    argyleBankValidation,
    plaidBankValidation,
    plaidPayrollValidation,
  };

  const profileControlledSections = PROFILE_CONTROLLED_VALIDATION_SECTIONS.reduce<PrefillSection[]>((sections, section) => {
    const shouldInclude =
      profileSections === undefined ||
      isProfileSectionEnabledForMessages(
        profileSections,
        section.profileSection,
        section.requiresDocuments
      );
    if (!shouldInclude) return sections;

    const existingSection = sections.find(({ label }) => label === section.label);
    const messages = section.getMessages(validationResponses);
    if (section.omitWhenEmpty && !messages.length) return sections;
    if (existingSection) {
      existingSection.messages.push(...messages);
    } else {
      sections.push({ label: section.label, messages });
    }
    return sections;
  }, []);

  const profileIndependentSections = PROFILE_INDEPENDENT_VALIDATION_SECTIONS.map((section) => ({
    label: section.label,
    messages: section.getMessages(validationResponses),
  }));

  return [...profileControlledSections, ...profileIndependentSections];
};

export const buildSendMessageValidationSectionsFromResponses = (
  profileSections: ProfileSection[] | undefined,
  validationResponses: ApplicantValidationResponses
): PrefillSection[] =>
  collectApplicantValidationSections(validationResponses, profileSections ?? []);

type BuildSectionedPrefillOptions = {
  includeBullets?: boolean;
};

export const buildSectionedPrefill = (
  sections: PrefillSection[],
  options: BuildSectionedPrefillOptions = {}
) => {
  const { includeBullets = true } = options;
  const sectionLines: string[] = [];

  sections.forEach(({ label, messages }) => {
    if (!messages || !messages.length) return;
    const seen = new Set<string>();
    const unique = messages.filter((msg) => {
      if (seen.has(msg)) return false;
      seen.add(msg);
      return true;
    });
    if (!unique.length) return;
    sectionLines.push(`${label}:`);
    unique.forEach((msg) => {
      sectionLines.push(includeBullets ? `• ${msg}` : msg);
    });
    sectionLines.push(''); // blank line after each section
  });

  if (!sectionLines.length) return '';
  const formatted: string[] = [MESSAGE_PREAMBLE, '', ...sectionLines]; // blank line after preamble
  return formatted.join('\n');
};
